import React, { useEffect, useState } from 'react';
import { FileText, Trash2, X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import {
  success,
  error as toastError,
  WarningDialog,
} from '@innovyze/stylovyze';
import { deleteContextNote, getContextNote } from '@/services/knowledgeApi';
import type { ContextItem } from '@Types/knowledge';
import {
  contextAvailabilityLabel,
  getIngestionStatus,
  knowledgeLibraryBlurb,
  sourceFileKind,
} from '@/utils/knowledgeStatus';

interface SourceDetailProps {
  contextId: string;
  readOnly?: boolean;
  onBack: () => void;
  onDeleted?: () => void;
}

const formatDate = (value: string | null) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return value;
  }
};

const formatBytes = (bytes: number | null) => {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

export const SourceDetail = ({
  contextId,
  readOnly = false,
  onBack,
  onDeleted,
}: SourceDetailProps) => {
  const dispatch = useDispatch();
  const [source, setSource] = useState<ContextItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = (showSpinner: boolean) => {
      if (showSpinner) setLoading(true);
      return getContextNote(contextId)
        .then(item => {
          if (!cancelled) setSource(item);
        })
        .catch(e => {
          if (!cancelled) {
            setLoadError(
              e instanceof Error ? e.message : 'Failed to load source',
            );
          }
        })
        .finally(() => {
          if (!cancelled && showSpinner) setLoading(false);
        });
    };

    load(true);
    return () => {
      cancelled = true;
    };
  }, [contextId]);

  useEffect(() => {
    if (!source || getIngestionStatus(source) !== 'processing') return undefined;
    const timer = window.setInterval(() => {
      getContextNote(contextId)
        .then(item => setSource(item))
        .catch(() => {
          /* keep last good state while polling */
        });
    }, 3000);
    return () => window.clearInterval(timer);
  }, [contextId, source?.ingestion_status, source?.context_id]);

  const handleDelete = async () => {
    if (!source || deleting) return;
    setDeleting(true);
    try {
      await deleteContextNote(source.context_id);
      dispatch(success(`Deleted "${source.name}"`));
      onDeleted?.();
    } catch (e) {
      dispatch(
        toastError(e instanceof Error ? e.message : 'Failed to delete source'),
      );
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#3C3C3C] border-t-transparent" />
      </div>
    );
  }

  if (loadError || !source) {
    return (
      <div className="max-w-[820px] mx-auto py-8">
        <p className="text-sm text-red-600">
          {loadError || 'Source not found'}
        </p>
        <button
          type="button"
          onClick={onBack}
          className="mt-3 text-sm text-[#007CA0] hover:underline">
          Back to Knowledge
        </button>
      </div>
    );
  }

  const ingestion = getIngestionStatus(source);
  const libraryBlurb = knowledgeLibraryBlurb(source);
  const sizeLabel = formatBytes(source.file_size_bytes);
  const metaLine = [
    sourceFileKind(source.source_filename),
    source.chunk_count
      ? `${source.chunk_count} passage${
          source.chunk_count === 1 ? '' : 's'
        } indexed`
      : null,
    sizeLabel,
    source.author_display_name
      ? `added by ${source.author_display_name}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="max-w-[1020px] mx-auto flex flex-col min-h-[70vh] bg-white rounded-lg border border-[#E4E9EC] overflow-hidden shadow-sm">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#EDF1F3] bg-white">
        <div className="h-8 w-8 rounded bg-[#F3F5F9] text-[#5B6B72] flex items-center justify-center">
          <FileText className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold text-[#252F33] truncate">
            {source.name}
          </div>
          <div className="text-[11.5px] text-[#768D95] mt-0.5 truncate">
            {metaLine}
            {source.created_at ? ` · ${formatDate(source.created_at)}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            className={`text-[11px] font-semibold rounded-full px-2.5 py-0.5 ${
              ingestion === 'processing'
                ? 'text-[#7A5B14] bg-[#FFF4D6]'
                : ingestion === 'failed'
                ? 'text-[#8B2E2E] bg-[#FCEAEA]'
                : 'text-[#4F6B58] bg-[#EEF4EF]'
            }`}>
            {contextAvailabilityLabel(source)}
          </span>
          {!readOnly && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="p-1.5 rounded hover:bg-red-50 text-[#768D95] hover:text-red-600"
              aria-label="Delete source">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onBack}
            className="p-1.5 rounded hover:bg-slate-100 text-[#768D95]"
            aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        <aside className="hidden sm:block w-[210px] flex-shrink-0 border-r border-[#EDF1F3] bg-[#FAFBFC] p-2.5">
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-[#E7EBEE] text-[12.5px] font-medium text-[#41525A]">
            <FileText className="h-3.5 w-3.5" />
            <span className="truncate">{source.name}</span>
          </div>
        </aside>

        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6">
          <div className="max-w-[640px]">
            {libraryBlurb ? (
              <p className="text-[13.5px] leading-relaxed text-[#41525A] mb-6">
                {libraryBlurb}
              </p>
            ) : null}

            {source.chunk_count ? (
              <div className="flex items-start gap-2 text-xs text-[#768D95] bg-[#FAFBFC] border border-[#E4E9EC] rounded-md px-3 py-2.5 mb-6">
                <span className="text-[#007CA0] font-bold">i</span>
                <span>
                  Read once when it was added. Its {source.chunk_count} passages
                  are indexed, so a question can reach the right one without
                  re-reading the document.
                </span>
              </div>
            ) : null}

            {source.source_prompt?.trim() && (
              <>
                <p className="text-[9.5px] font-bold uppercase tracking-wider text-[#768D95] mb-2">
                  Prompt used
                </p>
                <p className="text-sm text-[#41525A] mb-6 leading-relaxed">
                  {source.source_prompt.trim()}
                </p>
              </>
            )}

            {source.body && (
              <>
                <p className="text-[9.5px] font-bold uppercase tracking-wider text-[#768D95] mb-2">
                  Document preview
                </p>
                <pre className="text-xs leading-relaxed whitespace-pre-wrap rounded-md border border-[#E4E9EC] bg-[#FAFBFC] p-4 text-[#41525A] max-h-80 overflow-y-auto">
                  {source.body}
                </pre>
              </>
            )}
          </div>
        </div>
      </div>

      {confirmDelete && (
        <WarningDialog
          open={confirmDelete}
          title="Delete source?"
          cancelText="Cancel"
          confirmText={deleting ? 'Deleting...' : 'Delete'}
          onCancel={() => !deleting && setConfirmDelete(false)}
          onConfirm={handleDelete}>
          <p className="text-sm text-[#252F33]">
            {`"${source.name}" will be removed from your library.`}
          </p>
        </WarningDialog>
      )}
    </div>
  );
};
